import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';

const ServiceDetail = ({ title, description, features, image, ctaText, ctaLink }) => {
  // Function to handle smooth scrolling to anchor
  const handleScrollToSection = (e) => {
    // Only handle if it's an in-page anchor link
    if (ctaLink && ctaLink.startsWith('#')) {
      e.preventDefault();
      const targetId = ctaLink.substring(1);
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        window.scrollTo({
          top: targetElement.offsetTop - 120, // Increased offset to ensure headers are fully visible
          behavior: 'smooth'
        });
      }
    }
  };

  // Determine if we should use Link or anchor tag
  const isExternalLink = ctaLink && !ctaLink.startsWith('#');
  
  return (
    <ServiceDetailContainer>
      <ServiceImage style={{ backgroundImage: `url(${image})` }}>
        <ServiceImageOverlay />
      </ServiceImage>
      
      <ServiceContent>
        <ServiceTitle>{title}</ServiceTitle>
        <ServiceDescription>{description}</ServiceDescription>
        
        <ServiceFeatures>
          {features.map((feature, index) => (
            <ServiceFeature key={index}>
              <FeatureIcon>
                <FaCheck />
              </FeatureIcon>
              <FeatureText>{feature}</FeatureText>
            </ServiceFeature>
          ))}
        </ServiceFeatures>
        
        {isExternalLink ? (
          <ServiceCTALink to={ctaLink || '#contact'}>
            {ctaText || 'Contact Us'}
          </ServiceCTALink>
        ) : (
          <ServiceCTAButton href={ctaLink || '#contact'} onClick={handleScrollToSection}>
            {ctaText || 'Contact Us'}
          </ServiceCTAButton>
        )}
      </ServiceContent>
    </ServiceDetailContainer>
  );
};

const ServiceDetailContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceImage = styled.div`
  position: relative;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  
  @media (max-width: 992px) {
    height: 300px;
  }
`;

const ServiceImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(19, 19, 19, 0.2) 0%,
    rgba(19, 19, 19, 0.4) 100%
  );
`;

const ServiceContent = styled.div`
  padding: 2rem 0;
`;

const ServiceTitle = styled.h2`
  color: var(--color-tertiary);
  margin-bottom: 1.5rem;
  font-size: 2.5rem;
`;

const ServiceDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.8;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  margin-bottom: 2.5rem;
`;

const ServiceFeature = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const FeatureIcon = styled.div`
  color: var(--color-secondary);
  margin-right: 1rem;
  margin-top: 0.3rem;
`;

const FeatureText = styled.p`
  color: var(--color-tertiary);
  margin-bottom: 0;
`;

const ServiceCTALink = styled(Link)`
  display: inline-block;
  padding: 1rem 2.5rem;
  background-color: var(--color-primary);
  color: var(--color-tertiary);
  border: none;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--color-secondary);
    color: var(--color-dark);
  }
`;

const ServiceCTAButton = styled.a`
  display: inline-block;
  padding: 1rem 2.5rem;
  background-color: var(--color-primary);
  color: var(--color-tertiary);
  border: none;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: var(--transition);
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    background-color: var(--color-secondary);
    color: var(--color-dark);
  }
`;

export default ServiceDetail; 